
import axios from 'axios';
const debug = require('debug')('mtk:orders');

import React, { useMemo, useState } from 'react';
import store from 'store2';
export const session = store.namespace('user');
const TOKEN = 'X-Access-Token';

export const getToken = () => {
	return session.get(TOKEN);
};

import classnames from 'classnames';
import { pascal } from 'case';

import { routes } from '../routes';

import api from '../utils/api';
import { useQuery } from '../utils/query';

import { exportSpreadsheet } from '../utils/exportSpreadsheet';

import { roles, getImportOrdersColumnsByClientId, API_TARGET } from '../constants';
import { Loading } from './PageStatus';

import PageHeader from '../components/common/PageHeader';
import OrderList from '../components/OrderList';
import CourierOrderList from '../components/CourierOrderList';
import Pagination from '../components/common/Pagination';

import Select, { indexToOptions } from '../components/common/Select';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';


import { can } from '../can';

import css from './Orders.css';

const Orders = (props) => {
	const [modalOpen, setModalOpen] = useState(false);
	const [uploadOrdersFilename, setUploadOrdersFilename] = useState('');
	const [uploadResults, setUploadResults] = useState(null);
	const [uploadLoading, setUploadLoading] = useState(false);
	const [uploadError, setUploadError] = useState(null);
	const fileInputRef = React.useRef();

	const query = useQuery(props.location, props.history, { status: '!cancelled' });
	const { user } = api.auth();

	const { index, actions } = api.orders.index(query.obj);

	const { item: client, actions: clientActions } = api.clients.item(null, null, { lazy: true });
	const { actions: clientsActions } = api.clients.index(null, { lazy: true });

	const { actions: usersActions } = api.users.index(null, { lazy: true });
	const { actions: lockersActions } = api.lockers.index(null, { lazy: true });

	useMemo(() => {
		if (user.clientId) clientActions.get(null, null, user.clientId);
	}, [user.clientId]);

	// ACTIONS
	const getClients = async (search) => indexToOptions(await clientsActions.get({ search, perPage: 50 }));

	const getCouriers = async (search) =>
		indexToOptions(await usersActions.get({ search, role: 'courier', perPage: 50 }));

	const getLockers = async (search) => indexToOptions(await lockersActions.get({ search, perPage: 50 }));

	const exportXls = async () => {
		const limitDays = 90;

		const createdAfter = new Date();
		// date objects are mutable. we can set date directly in the created date.
		createdAfter.setDate(createdAfter.getDate() - limitDays);

		// the server expects strings, so we send the date param as a string
		const reportParams = {
			createdAfter: createdAfter.toISOString(),
			orderAsc: false,
			sortBy: 'createdAt',
		};
		const params = Object.assign(reportParams, query.obj);

		for (const key of Object.keys(params)) {
			if (!params[key] || params[key] == 'all') delete params[key];
		}
		const all = await actions.getAll(params);

		// XXX the customColumns prop should be taken from a preference stored in the client.
		// For the time being we should just hard code the customization functions
		// in the codebase, and refer to them by name on the client prefs.
		exportSpreadsheet("orders", all.toJS(), {
			clientId: params.clientId || user.clientId,
			filename: filenameFromParams('orders', params, user),
			extension: 'xls',
		});
	};

	const isCourier = !user.role || user.role == roles('courier');

	let showDropCodeSearch = false;
	let showPickupCodeSearch = false;
	let showPickupNationalIdSearch = false;
	let showPickupEmailSearch = false;
	let showPickupPhoneSearch = false;
	if (user.role == roles('admin')) {
		showDropCodeSearch = true;
		showPickupCodeSearch = true;
		showPickupNationalIdSearch = true;
		showPickupEmailSearch = true;
		showPickupPhoneSearch = true;
	}
	else if (client.data) {
		const prefs = client.data.get('preferences').toJS();
		if (prefs.searchByDropCode) showDropCodeSearch = true;
		if (prefs.searchByPickupNationalId) showPickupNationalIdSearch = true;
	}

	const openLoadExcelModal = () => {
		setModalOpen(true);
	};

	const closeLoadExcelModal = () => {
		setModalOpen(false);
		setUploadOrdersFilename('');
		setUploadResults(null);
		setUploadError(null);
		setUploadLoading(false);
	};

	const handleAttach = () => {
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
			fileInputRef.current.click();
		}
	};

	const lambdaEndpoint = "https://j2gl7ftx5alqbgevklkyco4eky0jcmin.lambda-url.us-east-1.on.aws/";

	const cid = query.obj.clientId || user.clientId;
	console.log('Import orders columns for client', cid, getImportOrdersColumnsByClientId(cid));
	const importOrdersColumns = getImportOrdersColumnsByClientId(cid);

	const postFileToLambda = async () => {
		const file = fileInputRef.current && fileInputRef.current.files[0];
		if (!file) {
			alert('Por favor, adjunte un archivo antes de confirmar.');
			return;
		}
		setUploadLoading(true);
		setUploadError(null);
		setUploadResults(null);

		try {
			const formData = new FormData();
			formData.append('file', file);
			formData.append('clientId', cid);
			formData.append('env', API_TARGET);
			formData.append('tkn', getToken());

			const res = await axios.post(lambdaEndpoint, formData, {
				headers: {
					'Content-Type': 'multipart/form-data'
				},
			});
			console.log('Lambda response:', res);
			if (res.data && Array.isArray(res.data.results)) {
				setUploadResults(res.data.results);
			} else if (res.data && res.data.results) {
				setUploadResults([res.data.results]);
			} else {
				setUploadResults([]);
			}
		} catch (err) {
			const errorMessage = err?.response?.data?.error || err?.message || 'Error desconocido';
			setUploadError(`Error al subir el archivo: ${errorMessage}`);
			setUploadResults(null);
			console.error('Error uploading file to Lambda:', errorMessage);
		} finally {
			setUploadLoading(false);
		}
		setUploadOrdersFilename('');
	}

	return (
		<main className={css.root}>
			<PageHeader title={isCourier ? 'Mis órdenes' : 'Órdenes'} {...index}>

				{can(user, 'export', 'orders') && (
					<>
						{importOrdersColumns && (
							<Button onClick={openLoadExcelModal}>
								Cargar Ordenes
							</Button>
						)}
						<small>Exportar últimos 90 días</small>
						<Button onClick={() => exportXls()}>Descargar .xls</Button>
					</>
				)}
				{can(user, 'create', 'orders') && (
					<Button disabled={!index.loaded} to={routes('newOrder')}>
						Nueva Orden
					</Button>
				)}
			</PageHeader>

			<Modal open={modalOpen} onClose={closeLoadExcelModal}>
				<div className={css.modalContent}>
					<h2>Cargar órdenes desde Excel</h2>

					<small>Seleccione un archivo .xlsx con las órdenes a cargar. Asegúrese de que las columnas estén correctamente configuradas según el siguiente esquema:</small>
					<table>
						<thead>
							<tr>
								{importOrdersColumns ? Object.keys(importOrdersColumns).map((key) => {
									if (key === 'extras') {
										return Object.keys(importOrdersColumns.extras).map((extraKey) => (
											<th key={extraKey}>{extraKey}|</th>
										));
									}
									return <th key={key}>{importOrdersColumns[key]} |</th>;
								}) : <th>Cargando esquema de columnas...</th>
								}
							</tr>
						</thead>
					</table>

					<br />

					<input
						ref={fileInputRef}
						type="file"
						accept=".xlsx, .xls"
						style={{ display: 'none' }}
						onChange={async e => {
							const file = e.target.files[0];
							setUploadOrdersFilename(file ? file.name : '');
						}}
					/>
					<Button
						onClick={handleAttach}
						className={css.modalPreview}
						variant="primary">
						Adjuntar archivo .xlsx
					</Button>

					<div
						style={{ marginTop: 8, fontStyle: 'italic', color: '#555' }}
					>
						{uploadOrdersFilename ? `Archivo seleccionado: ${uploadOrdersFilename}` : 'Ningún archivo seleccionado'}
					</div>

					<br />

					<Button
						onClick={postFileToLambda}
						disabled={!uploadOrdersFilename || uploadLoading}
						className={css.modalPreview}
						variant="primary">
						Subir y procesar
					</Button>

					{/* Preview and XLSX processing removed as requested */}
					{uploadLoading && (
						<div className={css.modalPreview}>
							<div style={{ color: '#0074D9', fontWeight: 500, marginBottom: 8 }}>
								Subiendo archivo y procesando en el servidor, por favor espere...
							</div>
						</div>
					)}
					{uploadError && <div style={{ color: 'red' }}>{uploadError}</div>}
					{uploadResults && (
						<div className={css.modalPreview}>
							<h3>Respuesta:</h3>
							<div
								className={css.tableScroll + ' ' + css.previewSmall}
								style={{ width: '100%', minWidth: 0, overflowX: 'auto', overflowY: 'auto', maxHeight: 350 }}
							>
								<table
									className={css.previewSmall}
									style={{ minWidth: 700, fontSize: '11px', lineHeight: 1.2 }}
								>
									<thead>
										<tr>
											<th style={{ padding: '2px 4px' }}>#</th>
											<th style={{ padding: '2px 4px' }}>ID Orden</th>
											<th style={{ padding: '2px 4px' }}>Respuesta</th>
											<th style={{ padding: '2px 4px' }}>Nombre</th>
											<th style={{ padding: '2px 4px' }}>Email</th>
											<th style={{ padding: '2px 4px' }}>Teléfono</th>
											<th style={{ padding: '2px 4px' }}>Estado</th>
											<th style={{ padding: '2px 4px' }}>Motivo</th>
										</tr>
									</thead>
									<tbody>
										{uploadResults.length === 0 && (
											<tr>
												<td colSpan={9} style={{ padding: '2px 4px', fontSize: '11px' }}>Sin resultados</td>
											</tr>
										)}
										{uploadResults.map((r, i) => {
											const o = r.dropPayload?.order || {};
											let resultMessage = '';
											if (r.result?.error) {
												resultMessage = `Error: ${r.result.error}`;
											} else if (r.result?.details?.message) {
												resultMessage = `Error: ${r.result.details.message}`;
											} else if (r.result) {
												resultMessage = 'Éxito';
											} else {
												resultMessage = 'Sin respuesta';
											}
											return (
												<tr key={i} style={{ fontSize: '11px' }}>
													<td style={{ padding: '2px 4px' }}>{i + 1}</td>
													<td style={{ padding: '2px 4px' }}>{o.id}</td>
													<td style={{ fontSize: '10px', maxWidth: 100, overflow: 'auto', wordBreak: 'break-all', padding: '2px 4px' }}>
														{resultMessage}
													</td>
													<td style={{ padding: '2px 4px' }}>{o.pickupName || ''}</td>
													<td style={{ padding: '2px 4px' }}>{o.pickupEmail || ''}</td>
													<td style={{ padding: '2px 4px' }}>{o.pickupPhone || ''}</td>
													<td style={{ padding: '2px 4px' }}>{o.extras?.estadoSolicitud || ''}</td>
													<td style={{ padding: '2px 4px' }}>{o.extras?.motivo || ''}</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>

						</div>
					)}

				</div>
			</Modal>

			<div className={css.toolBar}>
				<div className={css.filters}>
					{showDropCodeSearch && (
						<Input
							className={css.select}
							label="Buscar por Drop Code"
							name="searchDropCode"
							onChange={query.set}
							value={query.get('searchDropCode')}
							placeholder="Drop Code"
						/>
					)}
					{showPickupCodeSearch && (
						<Input
							className={css.select}
							label="Buscar por Pickup Code"
							name="searchPickupCode"
							onChange={query.set}
							value={query.get('searchPickupCode')}
							placeholder="Pickup Code"
						/>
					)
					}
					{showPickupNationalIdSearch && (
						<Input
							className={css.select}
							label="Buscar por Id Destino:"
							name="searchPickupNationalId"
							onChange={query.set}
							value={query.get('searchPickupNationalId')}
							placeholder="Rut"
						/>
					)}
					{showPickupPhoneSearch && (
						<Input
							className={css.select}
							label="Buscar por Teléfono Destino:"
							name="searchPickupPhone"
							onChange={query.set}
							value={query.get('searchPickupPhone')}
							placeholder="Teléfono"
						/>
					)}
					{showPickupEmailSearch && (
						<Input
							className={css.select}
							label="Buscar por Email Destino:"
							name="searchPickupEmail"
							onChange={query.set}
							value={query.get('searchPickupEmail')}
							placeholder="Email"
						/>
					)}

					<Select
						className={css.select}
						label="Tipo"
						name="orderType"
						value={query.get('orderType')}
						onChange={query.set}
						placeholder="Todas"
						isClearable={false}
						options={[
							{ label: 'Todas', value: '' },
							{ label: 'Envíos y revocaciones', value: 'delivery,reversal' },
							{ label: 'Solo envíos', value: 'delivery' },
							{ label: 'Solo revocaciones', value: 'reversal' },
							{ label: 'Devoluciones', value: 'return' },
							{ label: 'Custodias', value: 'custody' },
						]}
					/>
					<Select
						className={css.select}
						label="Estado"
						name="status"
						value={query.get('status')}
						onChange={query.set}
						placeholder="Todas"
						isClearable={false}
						options={[
							{ label: 'Todas', value: '!cancelled' },
							{ label: 'Activas', value: '!completed,!cancelled' },
							{ label: 'Creadas', value: 'created' },
							{ label: 'Reservadas', value: 'reserved' },
							{ label: 'Listas para retiro', value: 'ready' },
							{ label: 'Completadas', value: 'completed' },
							{ label: 'Canceladas', value: 'cancelled' },
						]}
					/>

					{can(user, 'index', 'clients') && (
						<Select
							className={css.select}
							label="Cliente"
							name="clientId"
							value={query.get('clientId')}
							onChange={query.set}
							placeholder="Todos"
							loadOptions={getClients}
						/>
					)}
					{can(user, 'index', 'users') && (
						<Select
							className={css.select}
							label="Couriers"
							name="courierId"
							value={query.get('courierId')}
							onChange={query.set}
							placeholder="Todos"
							loadOptions={getCouriers}
						/>
					)}
					{can(user, 'index', 'lockers') && (
						<Select
							className={css.select}
							label="Locker"
							name="lockerId"
							value={query.get('lockerId')}
							onChange={query.set}
							placeholder="Todos"
							loadOptions={getLockers}
						/>
					)}
					<Select
						className={css.select}
						label="Error"
						name="errored"
						isClearable={false}
						value={query.get('errored')}
						onChange={query.set}
						placeholder="Todas"
						options={[
							{ label: 'Todas', value: 'all' },
							{ label: 'Con errores', value: 'true' },
							{ label: 'Sin errores', value: 'false' },
						]}
					/>
				</div>
			</div>

			{index.loaded ? (
				isCourier ? (
					<CourierOrderList orders={index.data.toJS()} />
				) : (
					<OrderList
						className={classnames({ [css.listLoading]: index.loading })}
						orders={index.data.toJS()}
						addFilter={query.set}
						isAdmin={user.role == 'admin'}
						clientId={query.obj.clientId || user.clientId}
						showPickupNationalIdSearch={showPickupNationalIdSearch}
						showDropCodeSearch={showDropCodeSearch}
						showPickupEmailSearch={showPickupEmailSearch}
						showPickupPhoneSearch={showPickupPhoneSearch}
					/>
				)
			) : (
				<Loading compact />
			)}
			{index.pageInfo && <Pagination {...index.pageInfo.toJS()} onChange={query.setPage} />}
		</main>
	);
};

export default Orders;

/// HELPERS

const filenameFromParams = (basename, params = {}, user) => {
	let filename = basename;
	const clientId = params.clientId || user.clientId || 'allClients';
	for (const key of Object.keys(params)) {
		if (['page', 'perPage'].includes(key)) continue;
		filename += '-' + pascal(`${key}_${params[key].toString().replace('!', 'not_')}`);
	}

	return `client_${clientId}-${filename}`;
};
